/**
 * Static assets utilities for accessing CloudFront-backed images
 */

/**
 * Get the base URL for static assets (CloudFront CDN)
 */
export function getStaticAssetsUrl(): string {
  return process.env.STATIC_ASSETS_URL || 'https://static.mentorsub.com';
}

/**
 * Get the full URL for a static image
 * @param filename - The image filename (e.g., 'mentorsub-logo.png')
 * @returns Full URL to the image on CloudFront
 * 
 * @example
 * staticImageUrl('mentorsub-logo.png')
 * // => 'https://static.mentorsub.com/images/mentorsub-logo.png'
 */
export function staticImageUrl(filename: string): string {
  const baseUrl = getStaticAssetsUrl();
  return `${baseUrl}/images/${filename}`;
}

/**
 * Get the full URL for any static asset
 * @param path - The path relative to the static assets root (e.g., 'images/logo.png', 'fonts/roboto.woff2')
 * @returns Full URL to the asset on CloudFront
 * 
 * @example
 * staticAssetUrl('images/mentorsub-logo.png')
 * // => 'https://static.mentorsub.com/images/mentorsub-logo.png'
 */
export function staticAssetUrl(path: string): string {
  const baseUrl = getStaticAssetsUrl();
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${baseUrl}/${cleanPath}`;
}

/**
 * Get the URL for a public image - uses CDN in production, local path in development
 * This is for images that are served from both the Next.js public folder AND the CDN
 * 
 * @param path - The path relative to /public (e.g., 'images/mentorsub-logo.svg')
 * @returns CDN URL in production, local path in development
 * 
 * @example
 * publicImageUrl('images/mentorsub-logo.svg')
 * // Development: '/images/mentorsub-logo.svg'
 * // Production: 'https://static.mentorsub.com/images/mentorsub-logo.svg'
 */
export function publicImageUrl(path: string): string {
  // In production, use CDN for better performance
  if (process.env.NODE_ENV === 'production' && process.env.STATIC_ASSETS_URL) {
    return staticAssetUrl(path);
  }
  
  // In development, use local Next.js public folder
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return cleanPath;
}
